import moment from "moment-timezone";
import React from "react";
import ReactDOM from "react-dom";
import ReactExport from "react-export-excel";
import { columnData } from "../../components/teams/TeamsTable";
import { languages } from "../../constants/languages";
import SignupNameQuestion from "../../constants/SignupNameQuestion";
import { groupBy } from "../../util/GroupBy";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export const exportTeams = async (
  tournament,
  teamsContactInfo,
  divisionsObject
) => {
  try {
    // Get all teams first
    const teamsSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("teams")
      .get();

    const teams = [];
    teamsSnapshot.forEach((doc) => {
      let team = doc.data();
      team["id"] = doc.id;
      if (team.createdAt) {
        team["added"] = moment(team.createdAt.toDate()).unix();
      }

      // contactInfo
      team["email"] = (teamsContactInfo[team.id] || {}).email || "";
      team["questions"] = (teamsContactInfo[team.id] || {}).questions || {};
      Object.keys(tournament.infoFields || {}).forEach((key) => {
        if ((teamsContactInfo[team.id] || {})[key] !== undefined) {
          team[key] = (teamsContactInfo[team.id] || {})[key];
        }
      });

      teams.push(team);
    });

    // Get signup status for each team
    const signupStatuses = await Promise.all(
      teams.map(async (team) => {
        if (team.signupId) {
          const doc = await window.firebase
            .firestore()
            .collection("signupStatus")
            .doc(team.signupId)
            .get();
          return {
            teamId: team.id,
            status: doc.exists ? doc.data().status : 0,
            language: doc.exists ? doc.data().language || "" : "",
          };
        }
        return { teamId: team.id, status: 0, language: "" };
      })
    );

    // Get players for each team
    const playersData = await Promise.all(
      teams.map(async (team) => {
        const querySnapshot = await window.firebase
          .firestore()
          .collection("tournaments")
          .doc(tournament[".key"])
          .collection("players")
          .where("team", "==", team.id)
          .get();

        let players = [];
        querySnapshot.forEach((doc) => {
          players.push(doc.data());
        });

        players.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        return {
          teamId: team.id,
          playersString: players.map((p) => p.name).join(", "),
        };
      })
    );

    // Get login codes for each team
    const loginCodes = await Promise.all(
      teams.map(async (team) => {
        const querySnapshot = await window.firebase
          .firestore()
          .collection("tournaments")
          .doc(tournament[".key"])
          .collection("teamCodes")
          .where("teamId", "==", team.id)
          .where("type", "==", "team")
          .get();

        let code = "";
        querySnapshot.forEach((doc) => {
          code = doc.id;
        });

        let url =
          "https://" +
          (tournament.isPSD || tournament.crqpsd
            ? "tournify.prosoccerdata.com/"
            : "tournifyapp.com/live/") +
          tournament.liveLink;
        url += "/team/";
        url += team.id + "/" + code;

        return {
          teamId: team.id,
          loginURL: code && tournament.liveLink ? url : "",
        };
      })
    );

    // Prepare data for Excel export
    const teamsPerDivision = groupBy(teams, "division");

    // Get columns configuration
    let columns = columnData.filter((column) => {
      let show = ((tournament.defaultFields || {})[column.id] || {}).show;
      if (column.id === "refereeCountry" || column.id === "refereeShareURL") {
        return false;
      }
      if (
        show === undefined &&
        (column.id === "players" || column.id === "logo")
      ) {
        show = true;
      } else if (show === undefined) {
        show = false;
      }
      if (column.id === "name") {
        show = true;
      }
      if (column.id === "players" && tournament.individualPlayers) {
        show = false;
      }
      if (column.id === "present" && tournament.requireCheckIn) {
        show = true;
      }
      return show;
    });

    if (
      tournament.showTeamSignupQuestion2Table !== undefined
        ? tournament.showTeamSignupQuestion2Table
        : tournament.showTeamSignupQuestion2 !== undefined
        ? tournament.showTeamSignupQuestion2
        : false
    ) {
      columns.push({
        id: "gamertag",
        type: "question",
        label:
          tournament.teamSignupQuestion2 ||
          SignupNameQuestion.gamertagQuestion[
            (tournament.lang || [])[0]
              ? (tournament.lang || [])[0]
              : languages.getLanguage()
          ],
      });
    }

    // Add question columns
    Object.keys(tournament.questions || {})
      .filter((key) => tournament.questions[key].perSignup !== true)
      .sort((a, b) => {
        const qA = tournament.questions[a];
        const qB = tournament.questions[b];
        if (qA.num > qB.num) return 1;
        if (qA.num < qB.num) return -1;
        return 0;
      })
      .forEach((key) => {
        let question = tournament.questions[key];
        if (question.show) {
          columns.push({
            id: key + "q",
            type: "question",
            label: question.name,
          });
        }
      });

    Object.keys(tournament.questions || {})
      .filter((key) => tournament.questions[key].perSignup === true)
      .sort((a, b) => {
        const qA = tournament.questions[a];
        const qB = tournament.questions[b];
        if (qA.num > qB.num) return 1;
        if (qA.num < qB.num) return -1;
        return 0;
      })
      .forEach((key) => {
        let question = tournament.questions[key];
        if (question.show) {
          columns.push({
            id: key + "q",
            type: "question",
            label: question.name,
          });
        }
      });

    // Add product columns
    Object.keys(tournament.products || {})
      .filter((p) => !tournament.products[p].alwaysAdd)
      .sort((a, b) => {
        const pA = tournament.products[a];
        const pB = tournament.products[b];
        if (pA.num > pB.num) return 1;
        if (pA.num < pB.num) return -1;
        return 0;
      })
      .forEach((key) => {
        let product = tournament.products[key];
        if (product.show || product.show === undefined) {
          columns.push({ id: key + "p", type: "product", label: product.name });
        }
      });

    // Add info field columns
    Object.keys(tournament.infoFields || {}).forEach((key) => {
      let extraField = tournament.infoFields[key];
      columns.push({
        id: key + "i",
        type: "infoField",
        label: extraField.name,
      });
    });

    // Create Excel component with data
    const ExcelComponent = () => (
      <ExcelFile
        element={
          <button style={{ display: "none" }} id="downloadExcelButton">
            Download
          </button>
        }
        filename={
          tournament.individualPlayers
            ? languages.teams.players
            : languages.tournament.teams + " - " + tournament.name
        }
      >
        {Object.keys(teamsPerDivision)
          .sort((aKey, bKey) => {
            let a = divisionsObject[aKey] || {};
            let b = divisionsObject[bKey] || {};
            if (a.num > b.num) return 1;
            if (a.num < b.num) return -1;
            if (a.id > b.id) return 1;
            if (a.id < b.id) return -1;
            return 0;
          })
          .map((key) => {
            let division = divisionsObject[key] || {};
            let divisionTeams = teamsPerDivision[key].sort((a, b) => {
              function chunkify(t) {
                var tz = [],
                  x = 0,
                  y = -1,
                  n = 0,
                  i,
                  j;

                while ((i = (j = t.charAt(x++)).charCodeAt(0))) {
                  var m = i === 46 || (i >= 48 && i <= 57);
                  if (m !== n) {
                    tz[++y] = "";
                    n = m;
                  }
                  tz[y] += j;
                }
                return tz;
              }

              let toChunkA = a["name"] || "";
              let toChunkB = b["name"] || "";

              var aa = chunkify(toChunkA.toString().toLowerCase());
              var bb = chunkify(toChunkB.toString().toLowerCase());

              for (let x = 0; aa[x] && bb[x]; x++) {
                if (aa[x] !== bb[x]) {
                  var c = Number(aa[x]),
                    d = Number(bb[x]);
                  if (c === aa[x] && d === bb[x]) {
                    return c - d;
                  } else return aa[x] > bb[x] ? 1 : -1;
                }
              }
              return aa.length - bb.length;
            });

            let data = divisionTeams.map((team) => {
              const signupStatus = signupStatuses.find(
                (s) => s.teamId === team.id
              );
              const playerData = playersData.find((p) => p.teamId === team.id);
              const loginData = loginCodes.find((l) => l.teamId === team.id);

              let teamData = {
                present: team.present || false,
                payed: team.payed || false || signupStatus.status === 1,
                name: team.name,
                gamertag: team.gamertag || "",
                email: (teamsContactInfo[team.id] || {}).email || "",
                added: team.added
                  ? moment.unix(team.added).format(languages.teams.dateFormat)
                  : "",
                country: team.country || "",
                logo: team.logo || "",
                dressingRoom: team.dressingRoom || "",
                players: playerData.playersString,
                shareURL: loginData.loginURL,
                language: signupStatus.language,
              };

              let teamQuestions =
                (teamsContactInfo[team.id] || {}).questions || {};
              Object.keys(tournament.questions || {}).forEach((key) => {
                let question = tournament.questions[key];
                let answer = teamQuestions[key] || "";
                if (teamQuestions[key] !== undefined) {
                  teamData[key + "q"] =
                    question.type === "0"
                      ? answer
                      : (question.choices || {})[answer] || "";
                }
              });

              Object.keys(tournament.products || {}).forEach((key) => {
                let number = (team.products || {})[key] || "";
                teamData[key + "p"] = number;
              });

              Object.keys(tournament.infoFields || {}).forEach((key) => {
                if ((teamsContactInfo[team.id] || {})[key] !== undefined) {
                  teamData[key + "i"] = (teamsContactInfo[team.id] || {})[key];
                }
              });

              return teamData;
            });

            return (
              <ExcelSheet
                key={key}
                data={data}
                name={(division.name || "").replace(/[^a-zA-Z0-9 ]/g, "")}
              >
                {columns.map((column) => {
                  let label = column.label;
                  if (column.id === "present") {
                    label = languages.teams.present;
                  } else if (column.id === "payed") {
                    label = languages.teams.payed;
                  } else if (column.id === "shareURL") {
                    label = languages.teamInfo.shareURL;
                  }
                  return (
                    <ExcelColumn
                      key={column.id}
                      label={label}
                      value={
                        column.id === "country"
                          ? (col) => (col.country === "none" ? "" : col.country)
                          : (col) => col[column.id] || ""
                      }
                    />
                  );
                })}
              </ExcelSheet>
            );
          })}
      </ExcelFile>
    );

    // Create a temporary div and render the Excel component
    const tempDiv = document.createElement("div");
    document.body.appendChild(tempDiv);

    // Render the Excel component
    ReactDOM.render(<ExcelComponent />, tempDiv);

    // Trigger the download by clicking the hidden button
    setTimeout(() => {
      const downloadButton = document.getElementById("downloadExcelButton");
      if (downloadButton) {
        downloadButton.click();
      }

      // Cleanup
      setTimeout(() => {
        ReactDOM.unmountComponentAtNode(tempDiv);
        document.body.removeChild(tempDiv);
      }, 100);
    }, 0);
  } catch (error) {
    console.error("Error exporting teams:", error);
  }
};

// Wrapper component for backward compatibility
const ExportTeamsContainer = ({
  tournament,
  teamsContactInfo,
  divisionsObject,
  autoExport,
}) => {
  React.useEffect(() => {
    if (autoExport) {
      exportTeams(tournament, teamsContactInfo, divisionsObject);
    }
  }, [autoExport, tournament, teamsContactInfo, divisionsObject]);

  return null;
};

export default ExportTeamsContainer;
